import { useDeferredValue, useMemo, useState } from 'react';
import {
  Activity,
  Circle,
  Dumbbell,
  GitFork,
  HeartPulse,
  Percent,
  Scale,
  ScrollText,
  Search,
  ShoppingBasket,
  Sigma,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  findIntegration,
  NODE_CATALOG,
  searchNodeTypes,
  type NodeTypeDefinition,
  type NodeTypeMatch,
} from '@loggy/shared';

import { Badge, Card, Heading, Inline, Input, Stack, Text } from '../design/index.js';
import { navigateToDashboard } from './useHashRoute.js';
import styles from './NodeGuide.module.css';

/**
 * Everything this app can keep in orbit, and what each kind of thing gives
 * you — the page to read before adding a node, rather than picking from a
 * dropdown of nine names and hoping.
 *
 * Every word on it comes from the catalogue. A node type added there shows up
 * here with its title, its description, what its `kind` makes possible, and
 * whichever services feed it, without this file being touched — which is the
 * rule CLAUDE.md sets for adding something new to track, and a guide that
 * needed its own edit would quietly break it.
 */

/**
 * The catalogue names a Lucide icon; this resolves it to the component.
 *
 * An explicit map rather than Lucide's own `icons` record, which pulls every
 * icon in the set into the bundle. A node type whose icon is not listed here
 * still renders — it gets the fallback mark — so adding one stays a catalogue
 * edit, and giving it a proper icon is an optional line afterwards rather than
 * a prerequisite.
 */
const ICONS: Record<string, LucideIcon> = {
  scale: Scale,
  percent: Percent,
  dumbbell: Dumbbell,
  'heart-pulse': HeartPulse,
  activity: Activity,
  sigma: Sigma,
  'git-fork': GitFork,
  'scroll-text': ScrollText,
  'shopping-basket': ShoppingBasket,
};

/**
 * What each shape of node makes possible.
 *
 * Shown once, as a legend above the cards, rather than on each card. It is
 * true of every type of that kind, so repeating it put the same paragraph on
 * seven of the nine cards and pushed what was actually different about each
 * one below the fold.
 */
const KIND_SUMMARY: Record<NodeTypeDefinition['kind'], string> = {
  metric:
    'One number per moment. Charts every reading exactly as measured, and takes a goal you can watch the pace against.',
  collection:
    'A set of structured records that belong together, each with its own fields and its own place in the whole.',
  document: 'Long-form writing, with files and images attached to it.',
};

const KIND_LABEL: Record<NodeTypeDefinition['kind'], string> = {
  metric: 'Measurement',
  collection: 'Collection',
  document: 'Document',
};

/** Title-cases a catalogue `category`, which has no display name of its own. */
function categoryLabel(category: string): string {
  return category.charAt(0).toUpperCase() + category.slice(1);
}

/**
 * Says why a card is showing when the reason is not the title.
 *
 * A reader who typed "genelogie" and got Family tree should be able to see
 * that it matched "genealogy", rather than wondering whether the search is
 * guessing at them.
 */
function matchReason(match: NodeTypeMatch): string | null {
  if (match.matchedTerm === null) return null;
  if (match.fuzzy) return `closest to "${match.matchedTerm}"`;
  if (match.matchedOn === 'integration') return `works with ${match.matchedTerm}`;
  return `matched "${match.matchedTerm}"`;
}

/** The three shapes a node can take, so the badge on each card means something. */
function KindLegend() {
  return (
    <dl className={styles.legend}>
      {(Object.keys(KIND_LABEL) as NodeTypeDefinition['kind'][]).map((kind) => (
        <div className={styles.legendItem} key={kind}>
          <dt>
            <Badge variant="neutral">{KIND_LABEL[kind]}</Badge>
          </dt>
          <dd>
            <Text size="sm" color="muted">
              {KIND_SUMMARY[kind]}
            </Text>
          </dd>
        </div>
      ))}
    </dl>
  );
}

function NodeTypeCard({ match, showCategory }: { match: NodeTypeMatch; showCategory: boolean }) {
  const { definition } = match;
  const Icon = ICONS[definition.icon] ?? Circle;
  const integrations = definition.integrations ?? [];
  const reason = matchReason(match);

  return (
    <Card className={styles.card} data-testid={`guide-type-${definition.key}`}>
      <Stack gap={4}>
        <Inline gap={4} align="center">
          <span className={styles.mark} aria-hidden="true">
            <Icon size={18} strokeWidth={1.75} />
          </span>
          <Stack gap={1} className={styles.identity}>
            <Heading level={3} size="md">
              {definition.title}
            </Heading>
            <Inline gap={2} wrap align="center">
              <Badge variant="neutral">{KIND_LABEL[definition.kind]}</Badge>
              {/* Only while searching. Grouped, the category is already the
                  heading directly above this card. */}
              {showCategory && (
                <Text size="xs" color="muted">
                  {categoryLabel(definition.category)}
                </Text>
              )}
            </Inline>
          </Stack>
        </Inline>

        <Text size="sm" color="secondary">
          {definition.description}
        </Text>

        {/* The unit, and deliberately not a direction. Whether a number going
            down is good is the reader's to decide and nobody else's: the same
            falling body fat is progress to one person and a warning sign to
            another. The app says which way it moved, and only a goal the
            reader set themselves says whether that is the way they wanted. */}
        {definition.metric && (
          <dl className={styles.facts}>
            <div className={styles.fact}>
              <dt>
                <Text size="2xs" color="muted">
                  Measured in
                </Text>
              </dt>
              <dd>
                <Text size="sm" family="figure">
                  {definition.metric.unit.display}
                </Text>
              </dd>
            </div>
          </dl>
        )}

        <Stack gap={2} className={styles.integrations}>
          <Text size="2xs" color="muted" className={styles.sectionLabel}>
            Integrations
          </Text>
          {integrations.length === 0 ? (
            // Said plainly rather than left blank: an empty space reads as
            // "not documented", and this is a fact about the node type.
            <Text size="sm" color="muted">
              None yet — you keep this one yourself.
            </Text>
          ) : (
            <Stack gap={3}>
              {integrations.map((link) => {
                const integration = findIntegration(link.provider);
                if (!integration) return null;
                return (
                  <div key={link.provider} className={styles.integration}>
                    <Inline gap={2} align="center" wrap>
                      <Text size="sm" weight="medium">
                        {integration.name}
                      </Text>
                      {/* neutral, not positive: how data arrives is a
                          property of the service, not a state the reader
                          acts on, and the positive badge spends a reserved
                          status colour (see design/dsds/tokens/color-status). */}
                      <Badge variant="neutral">
                        {integration.delivery === 'push' ? 'Arrives on its own' : 'Fetched when you look'}
                      </Badge>
                    </Inline>
                    <Text as="p" size="sm" color="muted" className={styles.provides}>
                      {link.provides}
                    </Text>
                  </div>
                );
              })}
            </Stack>
          )}
        </Stack>

        {reason && (
          <Text size="2xs" color="muted" className={styles.reason}>
            {reason}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

export function NodeGuide() {
  const [query, setQuery] = useState('');
  // The catalogue is small enough to search on every keystroke, but deferring
  // keeps the field itself responsive if it ever stops being small.
  const deferredQuery = useDeferredValue(query);
  const searching = deferredQuery.trim().length > 0;

  const matches = useMemo(() => searchNodeTypes(deferredQuery), [deferredQuery]);

  /**
   * At rest the guide is a catalogue, grouped the way the dashboard groups
   * things. While searching it is a ranked list, because the ranking is the
   * answer and re-grouping it would bury the best match under a heading.
   */
  const grouped = useMemo(() => {
    const groups = new Map<string, NodeTypeMatch[]>();
    for (const match of matches) {
      const existing = groups.get(match.definition.category);
      if (existing) existing.push(match);
      else groups.set(match.definition.category, [match]);
    }
    return [...groups.entries()];
  }, [matches]);

  return (
    <Stack gap={8} className={styles.page} data-testid="node-guide">
      <Stack gap={4}>
        <button type="button" className={styles.back} onClick={navigateToDashboard} data-testid="guide-back">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 6l-6 6 6 6" />
          </svg>
          Orbit
        </button>
        <Heading level={1} className={styles.title}>
          What you can track
        </Heading>
        <Text size="sm" color="secondary" className={styles.lede}>
          Every kind of thing this app can keep in orbit, what each one gives you, and which
          services can fill it in for you.
        </Text>
      </Stack>

      <KindLegend />

      <div className={styles.searchRow}>
        <label htmlFor="node-guide-search" className={styles.srOnly}>
          Search node types
        </label>
        <span className={styles.searchIcon} aria-hidden="true">
          <Search size={16} strokeWidth={1.75} />
        </span>
        <Input
          id="node-guide-search"
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Try genealogy, blood pressure, or Withings"
          className={styles.search}
          data-testid="guide-search"
          autoComplete="off"
        />
      </div>

      {/* Polite rather than assertive: the count changes on every keystroke,
          and a screen reader interrupting each one is unusable. */}
      <Text
        as="p"
        size="xs"
        color="muted"
        role="status"
        aria-live="polite"
        className={styles.count}
        data-testid="guide-count"
      >
        {searching
          ? `${matches.length} of ${NODE_CATALOG.length} ${matches.length === 1 ? 'type' : 'types'} match “${deferredQuery.trim()}”`
          : `${NODE_CATALOG.length} types`}
      </Text>

      {matches.length === 0 ? (
        <Card className={styles.empty} data-testid="guide-empty">
          <Stack gap={3}>
            <Heading level={2} size="md">
              Nothing matches that
            </Heading>
            <Text size="sm" color="secondary">
              The search looks at each type&rsquo;s name, what it records, the unit it uses and the
              services that feed it — and it forgives a misspelling. If nothing came back, this app
              probably cannot track that yet.
            </Text>
            <Text size="sm" color="muted">
              A custom number can hold almost anything you want to count yourself.
            </Text>
          </Stack>
        </Card>
      ) : searching ? (
        <div className={styles.grid}>
          {matches.map((match) => (
            <NodeTypeCard key={match.definition.key} match={match} showCategory />
          ))}
        </div>
      ) : (
        <Stack gap={10}>
          {grouped.map(([category, group]) => (
            <Stack gap={4} key={category}>
              <Text size="xs" color="muted" className={styles.sectionLabel}>
                {categoryLabel(category)}
              </Text>
              <div className={styles.grid}>
                {group.map((match) => (
                  <NodeTypeCard key={match.definition.key} match={match} showCategory={false} />
                ))}
              </div>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
